import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Topbar from '../../components/Topbar';
import PageHeader from '../../components/PageHeader';
import { AIOutlineButton } from '../../components/ui/AIOutlineButton';
import { AISidebar } from '../../components/AISidebar';
import { AIRecommendationPanel } from '../../components/ai/AIRecommendationPanel';
import { getLMSProgrammingRecommendations } from '../../utils/aiLMSRecommendations';
import { Plus, Calendar, BarChart2, Trophy, MoreHorizontal, Clock, MapPin, Users, X, Star, Gem, Edit, Trash2, Eye } from 'lucide-react';
// Mock data
const mockPolls = [{
  id: 1,
  title: 'Preferenze mobilità',
  type: 'Standard',
  status: 'Pubblicato',
  updatedAt: 'Oggi, 10:30',
  question: 'Quale mezzo usi di più?',
  options: ['Auto', 'Bici', 'Mezzi pubblici', 'A piedi']
}, {
  id: 2,
  title: 'Abitudini alimentari',
  type: 'Giornaliero',
  status: 'Programmato',
  updatedAt: 'Ieri, 15:45',
  question: 'Quanti pasti bio consumi?',
  options: ['0-1', '2-3', '4-5', 'Più di 5']
}, {
  id: 3,
  title: 'Feedback app',
  type: 'Standard',
  status: 'Bozza',
  updatedAt: '2 giorni fa',
  question: "Come valuti l'app?",
  options: ['Ottima', 'Buona', 'Sufficiente', 'Da migliorare']
}];
const mockChallenges = [{
  id: 1,
  name: 'Zero Waste Week',
  duration: '7 giorni',
  pointsType: 'Star',
  points: 500,
  status: 'Attiva',
  description: 'Riduci i tuoi rifiuti per una settimana intera'
}, {
  id: 2,
  name: 'Bike to Work',
  duration: '30 giorni',
  pointsType: 'Diamond',
  points: 50,
  status: 'Bozza',
  description: 'Vai al lavoro in bici per un mese'
}];
const mockEvents = [{
  id: 1,
  title: 'Workshop Compostaggio',
  date: '15 Maggio, 18:00',
  location: 'Milano, Green Hub',
  spots: '25/30',
  status: 'Pubblicato',
  description: 'Impara a fare il compost domestico'
}, {
  id: 2,
  title: 'Meetup Community',
  date: '22 Maggio, 19:30',
  location: 'Roma, Eco Space',
  spots: '12/50',
  status: 'Pubblicato',
  description: 'Incontro mensile della community'
}, {
  id: 3,
  title: 'Pulizia Parco',
  date: '1 Giugno, 09:00',
  location: 'Torino, Parco Valentino',
  spots: '0/100',
  status: 'Bozza',
  description: 'Pulizia collettiva del parco'
}];
const LMSProgramming = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('eventi');
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState<'poll' | 'challenge' | 'event' | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const recommendations = getLMSProgrammingRecommendations().map(rec => ({
    ...rec,
    onCtaClick: () => console.log('CTA clicked:', rec.id)
  }));
  // Check if we should open the drawer from navigation state
  useEffect(() => {
    if (location.state?.openDrawer) {
      const type = location.state?.type || 'poll';
      setDrawerType(type);
      setActiveTab(type === 'poll' ? 'sondaggi' : type === 'challenge' ? 'challenge' : 'eventi');
      setShowCreateDrawer(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  const handleCreate = () => {
    setDrawerType(activeTab === 'sondaggi' ? 'poll' : activeTab === 'challenge' ? 'challenge' : 'event');
    setShowCreateDrawer(true);
  };
  const handleCreatePoll = () => {
    setActiveTab('sondaggi');
    setDrawerType('poll');
    setShowCreateDrawer(true);
  };
  const handleViewDetail = (item: any, type: 'poll' | 'challenge' | 'event') => {
    setSelectedItem(item);
    setDrawerType(type);
    setShowDetailDrawer(true);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pubblicato':
      case 'Attiva':
        return 'bg-green-100 text-green-800';
      case 'Programmato':
        return 'bg-blue-100 text-blue-800';
      case 'Bozza':
        return 'bg-gray-100 text-gray-800';
      case 'Terminata':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-2892">
      <div className="w-20 flex-shrink-0" data-id="element-2893">
        <Sidebar onCreatePoll={handleCreatePoll} data-id="element-2894" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2895">
        <Topbar data-id="element-2896" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2897">
          {/* Page Header with AI Button */}
          <div className="mb-8" data-id="element-2898">
            <div className="flex items-start justify-between" data-id="element-2899">
              <div data-id="element-2900">
                <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2901">
                  Programmazione
                </h1>
                <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-2902">
                  Gestisci sondaggi, challenge ed eventi
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-2903">
                <button onClick={handleCreate} className="inline-flex items-center px-4 py-2 bg-greenback text-white text-sm font-medium hover:bg-greenback/90 transition-colors" style={{
                borderRadius: '100px'
              }} data-id="element-2904">
                  <Plus className="h-4 w-4 mr-2" data-id="element-2905" />
                  Crea{' '}
                  {activeTab === 'sondaggi' ? 'sondaggio' : activeTab === 'challenge' ? 'challenge' : 'evento'}
                </button>
                <AIOutlineButton onClick={() => setIsAISidebarOpen(true)} data-id="element-2906">
                  Suggerimenti AI
                </AIOutlineButton>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6" data-id="element-2907">
            <nav className="-mb-px flex space-x-8" data-id="element-2908">
              {['Eventi IRL', 'Challenge', 'Sondaggi'].map(tab => {
              const tabKey = tab === 'Sondaggi' ? 'sondaggi' : tab === 'Challenge' ? 'challenge' : 'eventi';
              return <button key={tab} onClick={() => setActiveTab(tabKey)} className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tabKey ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    `} data-id="element-2909">
                    {tab}
                  </button>;
            })}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'sondaggi' && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2910">
              <table className="w-full" data-id="element-2911">
                <thead className="bg-gray-50" data-id="element-2912">
                  <tr data-id="element-2913">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2914">
                      Titolo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2915">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2916">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2917">
                      Ultimo aggiornamento
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2918">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-2919">
                  {mockPolls.map(poll => <tr key={poll.id} className="hover:bg-gray-50" data-id="element-2920">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900" data-id="element-2921">
                        {poll.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2922">
                        {poll.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2923">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(poll.status)}`} data-id="element-2924">
                          {poll.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2925">
                        {poll.updatedAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2926">
                        <button onClick={() => handleViewDetail(poll, 'poll')} className="text-green-600 hover:text-green-700" data-id="element-2927">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}

          {activeTab === 'challenge' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2928">
              {mockChallenges.map(challenge => <div key={challenge.id} onClick={() => handleViewDetail(challenge, 'challenge')} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer" data-id="element-2929">
                  <div className="flex justify-between items-start mb-4" data-id="element-2930">
                    <div data-id="element-2931">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-2932">
                        {challenge.name}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2933">
                        Durata: {challenge.duration}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(challenge.status)}`} data-id="element-2934">
                      {challenge.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-4" data-id="element-2935">
                    <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${challenge.pointsType === 'Diamond' ? 'bg-purple-100 text-purple-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-2936">
                      {challenge.pointsType === 'Diamond' ? <Gem className="h-3 w-3" data-id="element-2937" /> : <Star className="h-3 w-3" data-id="element-2938" />}
                      {challenge.points} Crediti{' '}
                      {challenge.pointsType === 'Diamond' ? 'Diamond' : 'Star'}
                    </span>
                    {challenge.pointsType === 'Diamond' && <span className="text-xs text-gray-500" data-id="element-2939">
                        Prova richiesta
                      </span>}
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-2" data-id="element-2940">
                    <div className="bg-green-600 h-2 rounded-full" style={{
                width: '45%'
              }} data-id="element-2941"></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500" data-id="element-2942">
                    <span data-id="element-2943">124 partecipanti</span>
                    <span data-id="element-2944">45% completamento</span>
                  </div>
                </div>)}
            </div>}

          {activeTab === 'eventi' && <div className="space-y-6" data-id="element-2945">
              {/* Featured Event */}
              {mockEvents[0] && <div onClick={() => handleViewDetail(mockEvents[0], 'event')} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100 p-6 cursor-pointer hover:shadow-md transition-shadow" data-id="element-2946">
                  <div className="flex justify-between items-start" data-id="element-2947">
                    <div data-id="element-2948">
                      <span className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2 block" data-id="element-2949">
                        Prossimo evento
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2950">
                        {mockEvents[0].title}
                      </h3>
                      <div className="flex gap-4 text-sm text-gray-600 mb-4" data-id="element-2951">
                        <span className="flex items-center gap-1" data-id="element-2952">
                          <Calendar className="h-4 w-4" data-id="element-2953" /> {mockEvents[0].date}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-2954">
                          <MapPin className="h-4 w-4" data-id="element-2955" />{' '}
                          {mockEvents[0].location}
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(mockEvents[0].status)}`} data-id="element-2956">
                      {mockEvents[0].status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2957">
                    <Users className="h-4 w-4 text-gray-500" data-id="element-2958" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-2959">
                      {mockEvents[0].spots} posti prenotati
                    </span>
                  </div>
                </div>}

              {/* Events Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2960">
                <table className="w-full" data-id="element-2961">
                  <thead className="bg-gray-50" data-id="element-2962">
                    <tr data-id="element-2963">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2964">
                        Evento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2965">
                        Data e Luogo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2966">
                        Posti
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2967">
                        Stato
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2968">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-2969">
                    {mockEvents.slice(1).map(event => <tr key={event.id} className="hover:bg-gray-50" data-id="element-2970">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900" data-id="element-2971">
                          {event.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2972">
                          <div data-id="element-2973">{event.date}</div>
                          <div className="text-xs" data-id="element-2974">{event.location}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2975">
                          {event.spots}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2976">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(event.status)}`} data-id="element-2977">
                            {event.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2978">
                          <button onClick={() => handleViewDetail(event, 'event')} className="text-green-600 hover:text-green-700" data-id="element-2979">
                            Dettagli
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI: Programmazione" data-id="element-2980">
        <AIRecommendationPanel recommendations={recommendations} data-id="element-2981" />
      </AISidebar>

      {/* Create Drawer */}
      {showCreateDrawer && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowCreateDrawer(false)} data-id="element-2982" />
          <div className="fixed top-0 right-0 h-full w-full sm:w-[500px] bg-white shadow-2xl z-50 overflow-y-auto" data-id="element-2983">
            <div className="p-6" data-id="element-2984">
              <div className="flex items-center justify-between mb-6" data-id="element-2985">
                <h2 className="text-xl font-bold" data-id="element-2986">
                  {drawerType === 'poll' ? 'Crea Sondaggio' : drawerType === 'challenge' ? 'Crea Challenge' : 'Crea Evento'}
                </h2>
                <button onClick={() => setShowCreateDrawer(false)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-2987">
                  <X className="h-5 w-5" data-id="element-2988" />
                </button>
              </div>

              {/* Form Content based on type */}
              <div className="space-y-6" data-id="element-2989">
                {drawerType === 'poll' && <>
                    <div data-id="element-2990">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2991">
                        Titolo
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" placeholder="Es. Preferenze mobilità" data-id="element-2992" />
                    </div>
                    <div data-id="element-2993">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2994">
                        Domanda
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" placeholder="Quale mezzo usi di più?" data-id="element-2995" />
                    </div>
                    <div data-id="element-2996">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2997">
                        Opzioni
                      </label>
                      <div className="space-y-2" data-id="element-2998">
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Opzione 1" data-id="element-2999" />
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Opzione 2" data-id="element-3000" />
                        <button className="text-sm text-green-600 font-medium hover:underline" data-id="element-3001">
                          + Aggiungi opzione
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3002">
                      <input type="checkbox" id="daily" className="rounded text-green-600 focus:ring-green-600" data-id="element-3003" />
                      <label htmlFor="daily" className="text-sm font-medium text-gray-700" data-id="element-3004">
                        Sondaggio del giorno
                      </label>
                    </div>
                  </>}

                {drawerType === 'challenge' && <>
                    <div data-id="element-3005">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3006">
                        Nome Challenge
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-3007" />
                    </div>
                    <div data-id="element-3008">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3009">
                        Descrizione
                      </label>
                      <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" rows={3} data-id="element-3010" />
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-3011">
                      <div data-id="element-3012">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3013">
                          Durata (giorni)
                        </label>
                        <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3014" />
                      </div>
                      <div data-id="element-3015">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3016">
                          Crediti
                        </label>
                        <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3017" />
                      </div>
                    </div>
                    <div data-id="element-3018">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3019">
                        Tipo Ricompensa
                      </label>
                      <div className="flex gap-4" data-id="element-3020">
                        <label className="flex items-center gap-2" data-id="element-3021">
                          <input type="radio" name="reward" className="text-green-600 focus:ring-green-600" data-id="element-3022" />
                          <Star className="h-4 w-4 text-amber-500" data-id="element-3023" />
                          <span className="text-sm" data-id="element-3024">Crediti Star</span>
                        </label>
                        <label className="flex items-center gap-2" data-id="element-3025">
                          <input type="radio" name="reward" className="text-green-600 focus:ring-green-600" data-id="element-3026" />
                          <Gem className="h-4 w-4 text-purple-500" data-id="element-3027" />
                          <span className="text-sm" data-id="element-3028">Crediti Diamond</span>
                        </label>
                      </div>
                    </div>
                  </>}

                {drawerType === 'event' && <>
                    <div data-id="element-3029">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3030">
                        Titolo Evento
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-3031" />
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-3032">
                      <div data-id="element-3033">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3034">
                          Data
                        </label>
                        <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3035" />
                      </div>
                      <div data-id="element-3036">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3037">
                          Ora
                        </label>
                        <input type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3038" />
                      </div>
                    </div>
                    <div data-id="element-3039">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3040">
                        Luogo
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Città, Indirizzo" data-id="element-3041" />
                    </div>
                    <div data-id="element-3042">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3043">
                        Posti disponibili
                      </label>
                      <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3044" />
                    </div>
                  </>}
              </div>

              {/* Footer Actions */}
              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200" data-id="element-3045">
                <button onClick={() => setShowCreateDrawer(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-id="element-3046">
                  Annulla
                </button>
                <button onClick={() => {
              alert('Elemento creato con successo!');
              setShowCreateDrawer(false);
            }} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-3047">
                  Pubblica
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Detail Drawer */}
      {showDetailDrawer && selectedItem && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowDetailDrawer(false)} data-id="element-3048" />
          <div className="fixed top-0 right-0 h-full w-full sm:w-[500px] bg-white shadow-2xl z-50 overflow-y-auto" data-id="element-3049">
            <div className="p-6" data-id="element-3050">
              <div className="flex items-center justify-between mb-6" data-id="element-3051">
                <h2 className="text-xl font-bold" data-id="element-3052">
                  {drawerType === 'poll' ? 'Dettagli Sondaggio' : drawerType === 'challenge' ? 'Dettagli Challenge' : 'Dettagli Evento'}
                </h2>
                <button onClick={() => setShowDetailDrawer(false)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-3053">
                  <X className="h-5 w-5" data-id="element-3054" />
                </button>
              </div>

              <div className="space-y-6" data-id="element-3055">
                {drawerType === 'poll' && <>
                    <div data-id="element-3056">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3057">
                        Titolo
                      </label>
                      <input type="text" defaultValue={selectedItem.title} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3058" />
                    </div>
                    <div data-id="element-3059">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3060">
                        Domanda
                      </label>
                      <input type="text" defaultValue={selectedItem.question} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3061" />
                    </div>
                    <div data-id="element-3062">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3063">
                        Opzioni
                      </label>
                      {selectedItem.options?.map((option: string, index: number) => <input key={index} type="text" defaultValue={option} className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2" data-id="element-3064" />)}
                    </div>
                    <div data-id="element-3065">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3066">
                        Stato
                      </label>
                      <select defaultValue={selectedItem.status} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3067">
                        <option value="Bozza" data-id="element-3068">Bozza</option>
                        <option value="Pubblicato" data-id="element-3069">Pubblicato</option>
                        <option value="Programmato" data-id="element-3070">Programmato</option>
                      </select>
                    </div>
                  </>}

                {drawerType === 'challenge' && <>
                    <div data-id="element-3071">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3072">
                        Nome
                      </label>
                      <input type="text" defaultValue={selectedItem.name} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3073" />
                    </div>
                    <div data-id="element-3074">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3075">
                        Descrizione
                      </label>
                      <textarea defaultValue={selectedItem.description} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" rows={3} data-id="element-3076" />
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-3077">
                      <div data-id="element-3078">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3079">
                          Durata
                        </label>
                        <input type="text" defaultValue={selectedItem.duration} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3080" />
                      </div>
                      <div data-id="element-3081">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3082">
                          Crediti
                        </label>
                        <input type="number" defaultValue={selectedItem.points} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3083" />
                      </div>
                    </div>
                    <div data-id="element-3084">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3085">
                        Stato
                      </label>
                      <select defaultValue={selectedItem.status} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3086">
                        <option value="Bozza" data-id="element-3087">Bozza</option>
                        <option value="Attiva" data-id="element-3088">Attiva</option>
                        <option value="Terminata" data-id="element-3089">Terminata</option>
                      </select>
                    </div>
                  </>}

                {drawerType === 'event' && <>
                    <div data-id="element-3090">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3091">
                        Titolo
                      </label>
                      <input type="text" defaultValue={selectedItem.title} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3092" />
                    </div>
                    <div data-id="element-3093">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3094">
                        Descrizione
                      </label>
                      <textarea defaultValue={selectedItem.description} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" rows={3} data-id="element-3095" />
                    </div>
                    <div data-id="element-3096">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3097">
                        Data e Ora
                      </label>
                      <input type="text" defaultValue={selectedItem.date} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3098" />
                    </div>
                    <div data-id="element-3099">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3100">
                        Luogo
                      </label>
                      <input type="text" defaultValue={selectedItem.location} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3101" />
                    </div>
                    <div data-id="element-3102">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3103">
                        Posti
                      </label>
                      <input type="text" defaultValue={selectedItem.spots} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-id="element-3104" />
                    </div>
                    <div data-id="element-3105">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3106">
                        Stato
                      </label>
                      <select defaultValue={selectedItem.status} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600" data-id="element-3107">
                        <option value="Bozza" data-id="element-3108">Bozza</option>
                        <option value="Pubblicato" data-id="element-3109">Pubblicato</option>
                      </select>
                    </div>
                  </>}
              </div>

              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200" data-id="element-3110">
                <button onClick={() => setShowDetailDrawer(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-id="element-3111">
                  Annulla
                </button>
                <button onClick={() => {
              alert('Modifiche salvate!');
              setShowDetailDrawer(false);
            }} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-3112">
                  Salva modifiche
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
};
export default LMSProgramming;